import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Check,
  User,
  Lock,
  Info,
  FileText,
  CalendarRange,
  ListChecks,
  Layers,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { ROLE_LABELS, PENGISI_SLOT } from "../../data/seedData";
import { hitungJumlahHari, uidSubPenugasan } from "../../utils/assignmentStorage";

function formatTanggal(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function PersonSelect({ label, options, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
        {label}
      </div>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={testId}
        className="block w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-[13px] text-slate-900 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
      >
        <option value="">{placeholder}</option>
        {options.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function SubPenugasanEditor({ items, onChange, ketuaList, anggotaList, userById }) {
  const [expanded, setExpanded] = useState(() => new Set());

  const addItem = () => {
    onChange([
      ...items,
      {
        id: uidSubPenugasan(),
        nama: "",
        tanggalMulai: "",
        tanggalSelesai: "",
        noSt: "",
        ketuaId: null,
        anggotaIds: [],
      },
    ]);
  };

  const updateItem = (id, patch) => {
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id) => {
    onChange(items.filter((it) => it.id !== id));
  };

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAnggotaOn = (it, id) => {
    const has = (it.anggotaIds || []).includes(id);
    if (has) {
      updateItem(it.id, { anggotaIds: it.anggotaIds.filter((x) => x !== id) });
    } else {
      if ((it.anggotaIds || []).length >= 3) return;
      updateItem(it.id, { anggotaIds: [...(it.anggotaIds || []), id] });
    }
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
          <Layers className="h-3 w-3" strokeWidth={2} />
          Sub Penugasan
        </div>
        <span className="text-[11px] font-medium text-slate-400">
          kegiatan tambahan / ST terpisah, mis. per kasus Dumas
        </span>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-center text-[11.5px] italic text-slate-400">
          Belum ada sub penugasan.
        </div>
      ) : (
        <div className="space-y-2" data-testid="sub-penugasan-list">
          {items.map((it, idx) => {
            const jh = hitungJumlahHari(it.tanggalMulai, it.tanggalSelesai);
            const isExpanded = expanded.has(it.id);
            const ketua = it.ketuaId ? userById[it.ketuaId] : null;
            const anggotaCount = (it.anggotaIds || []).length;
            return (
              <div
                key={it.id}
                data-testid={`sub-penugasan-item-${idx}`}
                className="rounded-md border border-gray-200 bg-white p-2.5"
              >
                <div className="flex items-start gap-2">
                  <input
                    type="text"
                    value={it.nama}
                    onChange={(e) => updateItem(it.id, { nama: e.target.value })}
                    placeholder="mis. Telaah Dumas Sdr. X — Dinas Y"
                    data-testid={`sub-penugasan-nama-${idx}`}
                    className="block w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-[12.5px] text-slate-900 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(it.id)}
                    data-testid={`sub-penugasan-remove-${idx}`}
                    className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 text-slate-400 hover:border-red-300 hover:text-red-600"
                    aria-label="Hapus sub penugasan"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                  </button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="date"
                    value={it.tanggalMulai}
                    onChange={(e) => updateItem(it.id, { tanggalMulai: e.target.value })}
                    data-testid={`sub-penugasan-mulai-${idx}`}
                    className="block w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-[12px] text-slate-900 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                  />
                  <span className="text-[10.5px] font-semibold text-slate-400">s.d.</span>
                  <input
                    type="date"
                    value={it.tanggalSelesai}
                    min={it.tanggalMulai || undefined}
                    onChange={(e) => updateItem(it.id, { tanggalSelesai: e.target.value })}
                    data-testid={`sub-penugasan-selesai-${idx}`}
                    className="block w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-[12px] text-slate-900 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                  />
                </div>
                {jh ? (
                  <div className="mt-1 text-[10.5px] font-medium text-[#1e3a5f]">{jh} hari</div>
                ) : null}

                <button
                  type="button"
                  onClick={() => toggleExpanded(it.id)}
                  data-testid={`sub-penugasan-toggle-detail-${idx}`}
                  className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-semibold text-slate-500 hover:text-[#1e3a5f]"
                >
                  {isExpanded ? (
                    <ChevronUp className="h-3 w-3" strokeWidth={2.2} />
                  ) : (
                    <ChevronDown className="h-3 w-3" strokeWidth={2.2} />
                  )}
                  No. ST &amp; Tim pelaksana
                  {!isExpanded && (it.noSt || ketua || anggotaCount > 0) ? (
                    <span className="font-normal text-slate-400">
                      ({[it.noSt, ketua?.name, anggotaCount ? `${anggotaCount} anggota` : null]
                        .filter(Boolean)
                        .join(" · ")}
                      )
                    </span>
                  ) : null}
                </button>

                {isExpanded ? (
                  <div className="mt-2 space-y-2 border-t border-gray-100 pt-2">
                    <input
                      type="text"
                      value={it.noSt || ""}
                      onChange={(e) => updateItem(it.id, { noSt: e.target.value })}
                      placeholder="No. ST untuk entri ini (opsional)"
                      data-testid={`sub-penugasan-no-st-${idx}`}
                      className="block w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-[12px] text-slate-900 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                    />
                    <select
                      value={it.ketuaId || ""}
                      onChange={(e) => updateItem(it.id, { ketuaId: e.target.value || null })}
                      data-testid={`sub-penugasan-ketua-${idx}`}
                      className="block w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-[12px] text-slate-900 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                    >
                      <option value="">Ketua Tim untuk entri ini (opsional)…</option>
                      {(ketuaList || []).map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                    <div>
                      <div className="mb-1 flex items-center justify-between text-[10px] text-slate-400">
                        <span>Anggota untuk entri ini (opsional, maks. 3)</span>
                        <span>{anggotaCount}/3</span>
                      </div>
                      <div className="max-h-32 overflow-y-auto rounded-md border border-gray-200">
                        {(anggotaList || []).map((a) => {
                          const selected = (it.anggotaIds || []).includes(a.id);
                          const disabled = !selected && anggotaCount >= 3;
                          return (
                            <button
                              type="button"
                              key={a.id}
                              onClick={() => toggleAnggotaOn(it, a.id)}
                              disabled={disabled}
                              data-testid={`sub-penugasan-anggota-${idx}-${a.id}`}
                              className={`flex w-full items-center justify-between border-b border-gray-100 px-2 py-1 text-left text-[11.5px] last:border-b-0 ${
                                selected
                                  ? "bg-[#e8edf2] text-[#1e3a5f]"
                                  : disabled
                                  ? "cursor-not-allowed text-slate-400"
                                  : "text-slate-700 hover:bg-gray-50"
                              }`}
                            >
                              {a.name}
                              {selected ? <Check className="h-3 w-3" strokeWidth={2.4} /> : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={addItem}
        data-testid="sub-penugasan-add"
        className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-dashed border-[#1e3a5f]/40 px-2.5 py-1.5 text-[11.5px] font-semibold text-[#1e3a5f] hover:border-[#1e3a5f] hover:bg-[#e8edf2]"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={2.4} />
        Tambah Sub Penugasan
      </button>
    </div>
  );
}

function ReadOnlyPanel({ sub, assignment, userById }) {
  const dalnis = assignment?.dalnisId ? userById[assignment.dalnisId] : null;
  const ketua = assignment?.ketuaId ? userById[assignment.ketuaId] : null;
  const anggotas = (assignment?.anggotaIds || [])
    .map((id) => userById[id])
    .filter(Boolean);
  const jumlahHari =
    assignment?.jumlahHari ??
    hitungJumlahHari(assignment?.tanggalMulai, assignment?.tanggalSelesai);

  const Row = ({ label, children }) => (
    <div className="flex items-start justify-between gap-3 border-b border-gray-100 py-2.5 last:border-b-0">
      <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
        {label}
      </span>
      <span className="max-w-[260px] text-right text-[12.5px] text-slate-800">
        {children}
      </span>
    </div>
  );

  const Person = ({ user }) =>
    user ? (
      <span className="inline-flex items-center gap-1.5">
        <User className="h-3 w-3 text-slate-400" strokeWidth={1.8} />
        {user.name}
      </span>
    ) : (
      <span className="italic text-slate-400">belum ditugaskan</span>
    );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <Lock className="h-3 w-3" />
        Akses hanya baca
      </div>

      <div className="rounded-md border border-gray-200 bg-white px-3 py-1">
        <Row label={ROLE_LABELS.DALNIS}>
          <Person user={dalnis} />
        </Row>
        <Row label={ROLE_LABELS.KETUA_TIM}>
          <Person user={ketua} />
        </Row>
        <Row label={`${ROLE_LABELS.ANGGOTA_TIM} (${anggotas.length}/3)`}>
          {anggotas.length === 0 ? (
            <span className="italic text-slate-400">belum ada</span>
          ) : (
            <span className="flex flex-col items-end gap-1">
              {anggotas.map((a) => (
                <Person key={a.id} user={a} />
              ))}
            </span>
          )}
        </Row>
        <Row label="No. ST">
          {assignment?.noSt ? (
            assignment.noSt
          ) : (
            <span className="italic text-slate-400">belum diisi</span>
          )}
        </Row>
        <Row label="Untuk">
          {assignment?.untuk ? (
            assignment.untuk
          ) : (
            <span className="italic text-slate-400">belum diisi</span>
          )}
        </Row>
        <Row label="Durasi Penugasan">
          {assignment?.tanggalMulai && assignment?.tanggalSelesai ? (
            <span>
              {formatTanggal(assignment.tanggalMulai)} s.d.{" "}
              {formatTanggal(assignment.tanggalSelesai)}
              {jumlahHari ? ` (${jumlahHari} hari)` : ""}
            </span>
          ) : (
            <span className="italic text-slate-400">belum diisi</span>
          )}
        </Row>
      </div>

      <div className="rounded-md border border-gray-200 bg-white px-3 py-2.5">
        <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
          <Layers className="h-3 w-3" strokeWidth={2} />
          Sub Penugasan
        </div>
        {(assignment?.subPenugasan || []).length === 0 ? (
          <span className="italic text-[12px] text-slate-400">belum ada</span>
        ) : (
          <ul className="space-y-1.5">
            {assignment.subPenugasan.map((sp) => {
              const jh =
                sp.jumlahHari ?? hitungJumlahHari(sp.tanggalMulai, sp.tanggalSelesai);
              const spKetua = sp.ketuaId ? userById[sp.ketuaId] : null;
              const spAnggotas = (sp.anggotaIds || []).map((id) => userById[id]).filter(Boolean);
              return (
                <li key={sp.id} className="text-[12.5px] text-slate-800">
                  <span className="font-medium">{sp.nama}</span>
                  {sp.tanggalMulai && sp.tanggalSelesai ? (
                    <span className="block text-[11px] text-slate-500">
                      {formatTanggal(sp.tanggalMulai)} s.d. {formatTanggal(sp.tanggalSelesai)}
                      {jh ? ` (${jh} hari)` : ""}
                    </span>
                  ) : null}
                  {sp.noSt ? (
                    <span className="block text-[11px] text-slate-500">No. ST: {sp.noSt}</span>
                  ) : null}
                  {spKetua || spAnggotas.length > 0 ? (
                    <span className="block text-[11px] text-slate-500">
                      {spKetua ? `Ketua: ${spKetua.name}` : ""}
                      {spKetua && spAnggotas.length > 0 ? " · " : ""}
                      {spAnggotas.length > 0
                        ? `Anggota: ${spAnggotas.map((a) => a.name).join(", ")}`
                        : ""}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex items-start gap-2 text-[11.5px] leading-relaxed text-slate-500">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Hanya peran <b>Irban</b> dan <b>Dalnis</b> yang dapat mengubah penugasan
          sub-kegiatan ini.
        </span>
      </div>
    </div>
  );
}

export default function AssignmentModal({
  open,
  onOpenChange,
  sub,
  assignment,
  users,
  canEdit,
  onSave,
}) {
  const [form, setForm] = useState(null);

  // Reset form when sub changes
  if (sub && (form === null || form._subId !== sub.id)) {
    setForm({
      _subId: sub.id,
      dalnisId: assignment?.dalnisId || null,
      ketuaId: assignment?.ketuaId || null,
      anggotaIds: assignment?.anggotaIds ? [...assignment.anggotaIds] : [],
      noSt: assignment?.noSt || "",
      untuk: assignment?.untuk || "",
      tanggalMulai: assignment?.tanggalMulai || "",
      tanggalSelesai: assignment?.tanggalSelesai || "",
      subPenugasan: (assignment?.subPenugasan || []).map((sp) => ({
        id: sp.id || uidSubPenugasan(),
        nama: sp.nama || "",
        tanggalMulai: sp.tanggalMulai || "",
        tanggalSelesai: sp.tanggalSelesai || "",
        noSt: sp.noSt || "",
        ketuaId: sp.ketuaId || null,
        anggotaIds: sp.anggotaIds ? [...sp.anggotaIds] : [],
      })),
    });
  }

  if (!sub || !form) return null;

  const safeUsers = Array.isArray(users) ? users : [];
  // ADMIN muncul di ketiga slot karena boleh merangkap peran operasional apa pun.
  // IRBAN tidak — ia penerima eskalasi, bukan pelaksana.
  const dalnisList = safeUsers.filter((u) => PENGISI_SLOT.DALNIS.includes(u.role));
  const ketuaList = safeUsers.filter((u) => PENGISI_SLOT.KETUA_TIM.includes(u.role));
  const anggotaList = safeUsers.filter((u) => PENGISI_SLOT.ANGGOTA_TIM.includes(u.role));

  const userById = {};
  safeUsers.forEach((u) => {
    if (u && u.id) userById[u.id] = u;
  });

  const toggleAnggota = (id) => {
    setForm((f) => {
      const has = f.anggotaIds.includes(id);
      if (has) {
        return { ...f, anggotaIds: f.anggotaIds.filter((x) => x !== id) };
      }
      if (f.anggotaIds.length >= 3) return f;
      return { ...f, anggotaIds: [...f.anggotaIds, id] };
    });
  };

  const jumlahHari = hitungJumlahHari(form.tanggalMulai, form.tanggalSelesai);
  const durasiInvalid =
    form.tanggalMulai && form.tanggalSelesai && !jumlahHari;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canEdit) return;
    onSave(sub.id, {
      dalnisId: form.dalnisId,
      ketuaId: form.ketuaId,
      anggotaIds: form.anggotaIds,
      noSt: form.noSt?.trim() || null,
      untuk: form.untuk?.trim() || null,
      tanggalMulai: form.tanggalMulai || null,
      tanggalSelesai: form.tanggalSelesai || null,
      subPenugasan: form.subPenugasan,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="assign-modal"
        className="max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            {sub.kategori ? (
              <span className="inline-flex h-5 items-center rounded bg-[#e8edf2] px-1.5 text-[10px] font-bold text-[#1e3a5f]">
                {sub.kategori}
              </span>
            ) : null}
            <span className="font-mono text-[11px] text-slate-500">{sub.id}</span>
          </div>
          <DialogTitle className="font-display text-base leading-snug tracking-tight">
            {sub.nama}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            {canEdit
              ? "Tentukan 1 Dalnis, 1 Ketua Tim, dan 1–3 Anggota Tim untuk sub-kegiatan ini."
              : "Tampilan detail penugasan sub-kegiatan (hanya baca)."}
          </DialogDescription>
        </DialogHeader>

        {!canEdit ? (
          <>
            <ReadOnlyPanel sub={sub} assignment={assignment} userById={userById} />
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="assign-close"
              >
                Tutup
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="assign-form">
            <PersonSelect
              label={`${ROLE_LABELS.DALNIS} (Pengendali Teknis)`}
              options={dalnisList}
              value={form.dalnisId}
              onChange={(v) => setForm((f) => ({ ...f, dalnisId: v }))}
              placeholder="Pilih Dalnis…"
              testId="assign-dalnis-select"
            />
            <PersonSelect
              label={ROLE_LABELS.KETUA_TIM}
              options={ketuaList}
              value={form.ketuaId}
              onChange={(v) => setForm((f) => ({ ...f, ketuaId: v }))}
              placeholder="Pilih Ketua Tim…"
              testId="assign-ketua-select"
            />

            <div>
              <div className="mb-1 flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                  {ROLE_LABELS.ANGGOTA_TIM} (maks. 3)
                </div>
                <div className="text-[11px] font-medium text-slate-500">
                  {form.anggotaIds.length}/3 dipilih
                </div>
              </div>
              <div
                className="max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white"
                data-testid="assign-anggota-list"
              >
                {anggotaList.map((a) => {
                  const selected = form.anggotaIds.includes(a.id);
                  const disabled = !selected && form.anggotaIds.length >= 3;
                  return (
                    <button
                      type="button"
                      key={a.id}
                      onClick={() => toggleAnggota(a.id)}
                      disabled={disabled}
                      data-testid={`assign-anggota-toggle-${a.id}`}
                      className={`flex w-full items-center justify-between border-b border-gray-100 px-3 py-2 text-left text-[12.5px] last:border-b-0 transition-colors ${
                        selected
                          ? "bg-[#e8edf2] text-[#1e3a5f]"
                          : disabled
                          ? "cursor-not-allowed text-slate-400"
                          : "text-slate-800 hover:bg-gray-50"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5" strokeWidth={1.8} />
                        {a.name}
                      </span>
                      {selected ? <Check className="h-4 w-4" strokeWidth={2.4} /> : null}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="block">
              <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                <FileText className="h-3 w-3" strokeWidth={2} />
                No. ST (Surat Tugas)
              </div>
              <input
                type="text"
                value={form.noSt}
                onChange={(e) => setForm((f) => ({ ...f, noSt: e.target.value }))}
                placeholder="mis. ST-123/IRB.SUS/08/2026"
                data-testid="assign-no-st"
                className="block w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-[13px] text-slate-900 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
              />
            </label>

            <label className="block">
              <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                <ListChecks className="h-3 w-3" strokeWidth={2} />
                Untuk
              </div>
              <textarea
                value={form.untuk}
                onChange={(e) => setForm((f) => ({ ...f, untuk: e.target.value }))}
                placeholder="mis. Melaksanakan audit atas…"
                rows={2}
                data-testid="assign-untuk"
                className="block w-full resize-none rounded-md border border-gray-300 bg-white px-2.5 py-2 text-[13px] text-slate-900 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
              />
            </label>

            <div>
              <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                <CalendarRange className="h-3 w-3" strokeWidth={2} />
                Durasi Penugasan
              </div>
              <div className="flex items-center gap-2">
                <label className="flex-1">
                  <div className="mb-1 text-[10px] text-slate-400">Dari</div>
                  <input
                    type="date"
                    value={form.tanggalMulai}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, tanggalMulai: e.target.value }))
                    }
                    data-testid="assign-tanggal-mulai"
                    className="block w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-[13px] text-slate-900 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                  />
                </label>
                <span className="mt-4 text-[11px] font-semibold text-slate-400">s.d.</span>
                <label className="flex-1">
                  <div className="mb-1 text-[10px] text-slate-400">Sampai dengan</div>
                  <input
                    type="date"
                    value={form.tanggalSelesai}
                    min={form.tanggalMulai || undefined}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, tanggalSelesai: e.target.value }))
                    }
                    data-testid="assign-tanggal-selesai"
                    className="block w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-[13px] text-slate-900 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                  />
                </label>
              </div>
              {durasiInvalid ? (
                <div className="mt-1 text-[11px] text-red-600">
                  Tanggal selesai harus setelah atau sama dengan tanggal mulai.
                </div>
              ) : jumlahHari ? (
                <div
                  className="mt-1 text-[11.5px] font-semibold text-[#1e3a5f]"
                  data-testid="assign-jumlah-hari"
                >
                  Jumlah hari: {jumlahHari} hari
                </div>
              ) : null}
            </div>

            <SubPenugasanEditor
              items={form.subPenugasan}
              onChange={(items) => setForm((f) => ({ ...f, subPenugasan: items }))}
              ketuaList={ketuaList}
              anggotaList={anggotaList}
              userById={userById}
            />

            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="assign-cancel"
              >
                Batal
              </Button>
              <Button
                type="submit"
                data-testid="assign-save"
                className="bg-[#1e3a5f] text-white hover:bg-[#152a46]"
              >
                Simpan Penugasan
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
